import {
  analyzeCustomerContactSource, sourceEmail, sourceKey, validSourceEmail,
} from "../../../sales/customers/CustomerContactRepairSourceEvidence";

// Customer company names are stricter than contact names: keep spaces and
// punctuation, otherwise "AB C" and "A BC" could silently share a repair batch.
const companyKey = (value) => String(value || "").trim().toLowerCase()
  .replace(/\s+/g, " ");

// Accept the two existing customer import workbook layouts without altering repair evidence.
export const normalizeContactRepairWorkbook = (rows = [], format = "elite-core") => {
  if (format !== "erp-import") return rows;
  return rows.filter((row) =>
    String(row["Record Type"] || "").trim().toUpperCase() === "CONTACT" &&
    String(row["Import?"] || "").trim().toUpperCase() === "Y"
  ).map((row) => ({
    "Row Type *": "CONTACT",
    "Client Name *": String(row["Client Name"] || "").trim(),
    "First Name *": String(row["First Name"] || "").trim(),
    "Last Name": String(row["Last Name"] || "").trim(),
    "Email *": String(row["Email"] || "").trim(),
  }));
};

// The same Elite Core client import format used by the single-customer popup.
export const groupContactRepairWorkbookRows = (rows = []) => {
  const groups = new Map();
  for (const row of rows) {
    if (String(row["Row Type *"] || "").trim().toUpperCase() !== "CONTACT") continue;
    const sourceName = String(row["Client Name *"] || "").trim();
    const nameKey = companyKey(sourceName);
    if (!nameKey) continue;
    if (!groups.has(nameKey)) groups.set(nameKey, { key: nameKey, sourceName, sourceRows: [] });
    groups.get(nameKey).sourceRows.push(row);
  }
  return [...groups.values()].sort((a, b) => a.sourceName.localeCompare(b.sourceName));
};

const identityName = (contact) =>
  sourceKey([contact?.firstName, contact?.lastName].filter(Boolean).join(" "));

// Shared mailboxes and imported identifiers are fine to preserve, but they
// are not trustworthy person-identity evidence for unattended bulk cleanup.
const requiresHumanIdentityReview = (contact, email) => {
  const first = String(contact?.firstName || "").trim();
  const full = [contact?.firstName, contact?.lastName].filter(Boolean).join(" ");
  const local = sourceEmail(email).split("@")[0];
  return /^(?:dn[emc]\d+|gst|ahs|provincial|edmteam)/i.test(first) ||
    /accounts payable|help desk|non po|do not remove|service desk/i.test(full) ||
    /^(?:info|sales|orders|support|help|billing|invoices|accounts|ap|ar|treasurer|noreply|no-reply|admin)(?:$|[._+-])/i.test(local);
};

const sourceEvidenceMaps = (rows) => {
  const emailsByName = new Map();
  const namesByEmail = new Map();
  for (const row of rows) {
    const name = sourceKey([row["First Name *"], row["Last Name"]].filter(Boolean).join(" "));
    const email = sourceEmail(row["Email *"]);
    if (!name || !validSourceEmail(email)) continue;
    if (!emailsByName.has(name)) emailsByName.set(name, new Set());
    emailsByName.get(name).add(email);
    if (!namesByEmail.has(email)) namesByEmail.set(email, new Set());
    namesByEmail.get(email).add(name);
  }
  const corroborates = (contact, email) => {
    const name = identityName(contact);
    const candidate = sourceEmail(email);
    return Boolean(name && candidate &&
      emailsByName.get(name)?.size === 1 &&
      emailsByName.get(name)?.has(candidate) &&
      namesByEmail.get(candidate)?.size === 1);
  };
  return { corroborates };
};

const ACTION_PREFIX = { merge: "merge:", recover: "recover:" };

// Suggestions that are ambiguous or carry saved document references are never
// batch-eligible; Administrators can still review them individually in the
// existing Contact Repair popup.
export const planBatchContactRepair = (preview, sourceRows = []) => {
  if (!preview?.contacts || !Array.isArray(preview.proposals)) return { actions: [], skipped: 0 };
  const { corroborates } = sourceEvidenceMaps(sourceRows);
  const used = new Set();
  const actions = [];
  const proposals = [...preview.proposals]
    .filter((pair) => pair.safe && pair.confidence === "strong" &&
      !requiresHumanIdentityReview(pair.result, pair.result?.email) &&
      corroborates(pair.result, pair.result?.email))
    .sort((a, b) => String(a.key).localeCompare(String(b.key)));
  for (const pair of proposals) {
    if (used.has(pair.keepId) || used.has(pair.removeId)) continue;
    used.add(pair.keepId);
    used.add(pair.removeId);
    actions.push({
      id: ACTION_PREFIX.merge + pair.key,
      kind: "merge", key: pair.key,
      keepId: pair.keepId, removeId: pair.removeId,
      label: [pair.result.firstName, pair.result.lastName].filter(Boolean).join(" "),
      email: sourceEmail(pair.result.email),
      description: "Consolidate matching contact records; retain the historical UUID as an inactive alias",
      before: `${pair.keep.firstName || ""} ${pair.keep.lastName || ""} · ${pair.keep.email || "No email"} / ${pair.remove.email || "No email"}`,
      after: `${[pair.result.firstName, pair.result.lastName].filter(Boolean).join(" ")} · ${pair.result.email}`,
    });
  }

  const evidence = analyzeCustomerContactSource(preview.contacts, sourceRows);
  const activeNameCounts = new Map();
  for (const contact of preview.contacts) {
    if (contact.mergedIntoContactId || !contact.isActive) continue;
    const name = identityName(contact);
    if (name) activeNameCounts.set(name, (activeNameCounts.get(name) || 0) + 1);
  }
  const proposedIds = new Set(preview.proposals.flatMap((pair) => [pair.keepId, pair.removeId]));
  for (const item of evidence.candidates) {
    const contact = preview.contacts.find((entry) => entry.contactId === item.contactId);
    if (item.state !== "email-not-in-core" || !item.email ||
        item.usageCount > 0 || !contact || !contact.isActive ||
        contact.linkedPortalAccount || contact.mergedIntoContactId ||
        item.titleEmail || requiresHumanIdentityReview(contact, item.email) ||
        used.has(item.contactId) ||
        proposedIds.has(item.contactId) ||
        activeNameCounts.get(identityName(contact)) !== 1 ||
        !corroborates(contact, item.email)) continue;
    used.add(item.contactId);
    actions.push({
      id: ACTION_PREFIX.recover + item.contactId + ":" + item.email,
      kind: "recover", contactId: item.contactId,
      firstName: contact.firstName, lastName: contact.lastName,
      label: item.name, email: item.email,
      description: "Fill one missing email from a uniquely matched source CONTACT row",
      before: `${item.name} · No email`, after: `${item.name} · ${item.email}`,
    });
  }
  return {
    actions,
    skipped: Math.max(0, (preview.proposals?.length || 0) +
      evidence.candidates.filter((c) => c.state !== "no-source-email").length - actions.length),
    reviewCount: preview.proposals.filter((pair) => pair.confidence === "review" || !pair.safe).length,
    sourceCount: sourceRows.length,
  };
};
